import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { contractsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  FileSignature, Plus, Search, Download, RefreshCcw, ChevronLeft, ChevronRight,
  AlertTriangle, Clock, FileText, Eye, Pencil, Trash2, Upload, AlertCircle, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow, differenceInDays, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONTRACT_TYPES = [
  { value: 'lease', label: 'Lease' },
  { value: 'service', label: 'Service' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'employment', label: 'Employment' },
  { value: 'nda', label: 'NDA' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'terminated', label: 'Terminated' },
  { value: 'draft', label: 'Draft' },
];

const SEVERITY_CONFIG = {
  critical: { label: 'Critical', color: 'bg-red-100 text-red-700', icon: AlertCircle, dotColor: 'bg-red-500' },
  warning: { label: 'Warning', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle, dotColor: 'bg-amber-500' },
  expired: { label: 'Expired', color: 'bg-red-100 text-red-700', icon: AlertCircle, dotColor: 'bg-red-500' },
  normal: { label: 'Normal', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2, dotColor: 'bg-emerald-500' },
  unknown: { label: '-', color: 'bg-slate-100 text-slate-500', icon: Clock, dotColor: 'bg-slate-400' },
};

const STATUS_BADGE = {
  active: 'bg-emerald-100 text-emerald-700',
  expired: 'bg-red-100 text-red-700',
  terminated: 'bg-slate-200 text-slate-600',
  draft: 'bg-blue-100 text-blue-700',
};

const emptyForm = {
  title: '', document_number: '', party_a: '', party_b: '',
  contract_type: 'other', start_date: '', expiration_date: '',
  status: 'active', notes: '',
};

const ContractsPage = () => {
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ contract_type: '', status: '', severity: '' });
  const [sortBy, setSortBy] = useState('expiration_date');
  const [sortOrder, setSortOrder] = useState('asc');
  const limit = 20;

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);

  const isAdmin = user?.role === 'tenant_admin' || user?.role === 'super_admin' || user?.role === 'facility_admin';

  const loadContracts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit, sort_by: sortBy, sort_order: sortOrder };
      if (search) params.search = search;
      if (filters.contract_type) params.contract_type = filters.contract_type;
      if (filters.status) params.status = filters.status;
      if (filters.severity) params.severity = filters.severity;
      const res = await contractsAPI.list(params);
      setContracts(res.data.contracts);
      setTotal(res.data.total);
    } catch (e) {
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  }, [page, search, filters, sortBy, sortOrder]);

  const loadStats = async () => {
    try {
      const res = await contractsAPI.stats();
      setStats(res.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { loadContracts(); }, [loadContracts]);
  useEffect(() => { loadStats(); }, []);

  const openCreate = () => {
    setForm({ ...emptyForm });
    setEditingId(null);
    setFileToUpload(null);
    setEditDialogOpen(true);
  };

  const openEdit = (c) => {
    setForm({
      title: c.title, document_number: c.document_number, party_a: c.party_a, party_b: c.party_b,
      contract_type: c.contract_type, start_date: c.start_date, expiration_date: c.expiration_date,
      status: c.status, notes: c.notes || '',
    });
    setEditingId(c.id);
    setFileToUpload(null);
    setEditDialogOpen(true);
  };

  const openDetail = (c) => {
    setSelectedContract(c);
    setDetailDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.document_number || !form.party_a || !form.party_b || !form.start_date || !form.expiration_date) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      let contractId;
      if (editingId) {
        await contractsAPI.update(editingId, form);
        contractId = editingId;
        toast.success('Contract updated');
      } else {
        const res = await contractsAPI.create(form);
        contractId = res.data.id;
        toast.success('Contract created');
      }
      if (fileToUpload && contractId) {
        await contractsAPI.upload(contractId, fileToUpload);
        toast.success('File uploaded');
      }
      setEditDialogOpen(false);
      loadContracts();
      loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save contract');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contract?')) return;
    try {
      await contractsAPI.delete(id);
      toast.success('Contract deleted');
      loadContracts();
      loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleExport = () => {
    const token = localStorage.getItem('token');
    const url = `${API_URL}/api/contracts/export`;
    const a = document.createElement('a');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        a.href = URL.createObjectURL(blob);
        a.download = `contracts_report_${format(new Date(), 'yyyyMMdd')}.xlsx`;
        a.click();
        toast.success('Report downloaded');
      })
      .catch(() => toast.error('Export failed'));
  };

  const handleDownloadFile = async (contractId, filename) => {
    try {
      const res = await contractsAPI.download(contractId);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'contract_file';
      a.click();
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const SeverityBadge = ({ severity, daysRemaining }) => {
    const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.unknown;
    return (
      <Badge className={`${config.color} border-0 gap-1 text-xs`}>
        <span className={`h-1.5 w-1.5 rounded-full ${config.dotColor}`} />
        {severity === 'expired' ? 'Expired' :
         severity === 'critical' ? `${daysRemaining}d (Critical)` :
         severity === 'warning' ? `${daysRemaining}d (Warning)` :
         daysRemaining != null ? `${daysRemaining}d` : '-'}
      </Badge>
    );
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="contracts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-3">
            <FileSignature className="h-8 w-8 text-indigo-600" />
            Contract Management
          </h1>
          <p className="text-slate-500 mt-1">Track contracts, leases, and agreements with expiration alerts.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleExport} data-testid="export-excel-btn">
            <Download className="mr-2 h-4 w-4" /> Export Excel
          </Button>
          {isAdmin && (
            <Button onClick={openCreate} data-testid="create-contract-btn">
              <Plus className="mr-2 h-4 w-4" /> New Contract
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-emerald-50"><CheckCircle2 className="h-5 w-5 text-emerald-600" /></div>
                <div>
                  <p className="text-2xl font-semibold text-slate-900" data-testid="stat-active">{stats.active}</p>
                  <p className="text-xs text-slate-500">Active Contracts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-amber-50"><AlertTriangle className="h-5 w-5 text-amber-600" /></div>
                <div>
                  <p className="text-2xl font-semibold text-amber-700" data-testid="stat-warning">{stats.warning}</p>
                  <p className="text-xs text-slate-500">Expiring 180 Days</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-red-50"><AlertCircle className="h-5 w-5 text-red-600" /></div>
                <div>
                  <p className="text-2xl font-semibold text-red-700" data-testid="stat-critical">{stats.critical}</p>
                  <p className="text-xs text-slate-500">Expiring 90 Days</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-slate-100"><Clock className="h-5 w-5 text-slate-600" /></div>
                <div>
                  <p className="text-2xl font-semibold text-slate-900" data-testid="stat-expired">{stats.expired}</p>
                  <p className="text-xs text-slate-500">Expired</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search contracts..." className="pl-9" value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }} data-testid="contract-search" />
            </div>
            <Select value={filters.contract_type || 'all'} onValueChange={(v) => { setFilters((f) => ({ ...f, contract_type: v === 'all' ? '' : v })); setPage(0); }}>
              <SelectTrigger className="w-[150px]" data-testid="filter-type"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {CONTRACT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.status || 'all'} onValueChange={(v) => { setFilters((f) => ({ ...f, status: v === 'all' ? '' : v })); setPage(0); }}>
              <SelectTrigger className="w-[140px]" data-testid="filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.severity || 'all'} onValueChange={(v) => { setFilters((f) => ({ ...f, severity: v === 'all' ? '' : v })); setPage(0); }}>
              <SelectTrigger className="w-[160px]" data-testid="filter-severity"><SelectValue placeholder="All Expirations" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Expirations</SelectItem>
                <SelectItem value="critical">Critical (≤90d)</SelectItem>
                <SelectItem value="warning">Warning (≤180d)</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={() => { loadContracts(); loadStats(); }} data-testid="refresh-btn">
              <RefreshCcw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : contracts.length === 0 ? (
            <div className="text-center py-20">
              <FileSignature className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No contracts found</h3>
              <p className="text-sm text-slate-400 mt-1">Create your first contract or adjust filters.</p>
              {isAdmin && <Button onClick={openCreate} className="mt-4"><Plus className="mr-2 h-4 w-4" />New Contract</Button>}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold uppercase w-[100px]">Doc #</TableHead>
                  <TableHead className="text-xs font-semibold uppercase">Title</TableHead>
                  <TableHead className="text-xs font-semibold uppercase">Parties</TableHead>
                  <TableHead className="text-xs font-semibold uppercase">Type</TableHead>
                  <TableHead className="text-xs font-semibold uppercase">Start</TableHead>
                  <TableHead className="text-xs font-semibold uppercase">Expiration</TableHead>
                  <TableHead className="text-xs font-semibold uppercase">Status</TableHead>
                  <TableHead className="text-xs font-semibold uppercase text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contracts.map((c) => {
                  const rowClass = c.expiration_severity === 'critical' || c.expiration_severity === 'expired'
                    ? 'bg-red-50/50 hover:bg-red-50'
                    : c.expiration_severity === 'warning'
                    ? 'bg-amber-50/50 hover:bg-amber-50'
                    : 'hover:bg-slate-50';
                  return (
                    <TableRow key={c.id} className={`${rowClass} transition-colors`} data-testid={`contract-row-${c.id}`}>
                      <TableCell className="font-mono text-sm text-slate-600">{c.document_number}</TableCell>
                      <TableCell>
                        <button className="text-sm font-medium text-indigo-600 hover:underline text-left" onClick={() => openDetail(c)}>
                          {c.title}
                        </button>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        <span>{c.party_a}</span>
                        <span className="text-slate-400 mx-1">&amp;</span>
                        <span>{c.party_b}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize">{c.contract_type?.replace(/_/g, ' ')}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{c.start_date && format(parseISO(c.start_date), 'MMM d, yyyy')}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="text-sm text-slate-700">{c.expiration_date && format(parseISO(c.expiration_date), 'MMM d, yyyy')}</span>
                          <SeverityBadge severity={c.expiration_severity} daysRemaining={c.days_remaining} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_BADGE[c.status] || 'bg-slate-100'} border-0 text-xs capitalize`}>{c.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDetail(c)} title="View Details">
                            <Eye className="h-4 w-4 text-slate-500" />
                          </Button>
                          {isAdmin && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)} title="Edit">
                                <Pencil className="h-4 w-4 text-slate-500" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(c.id)} title="Delete">
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100">
            <p className="text-sm text-slate-500">Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="prev-page"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-sm text-slate-600">Page {page + 1} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} data-testid="next-page"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Contract' : 'New Contract'}</DialogTitle>
            <DialogDescription>{editingId ? 'Update contract details' : 'Add a new contract or agreement'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Office Lease Agreement" data-testid="form-title" />
              </div>
              <div className="space-y-2">
                <Label>Document Number *</Label>
                <Input value={form.document_number} onChange={(e) => setForm((f) => ({ ...f, document_number: e.target.value }))} placeholder="CTR-001" data-testid="form-doc-number" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Party A *</Label>
                <Input value={form.party_a} onChange={(e) => setForm((f) => ({ ...f, party_a: e.target.value }))} placeholder="Your Company" data-testid="form-party-a" />
              </div>
              <div className="space-y-2">
                <Label>Party B *</Label>
                <Input value={form.party_b} onChange={(e) => setForm((f) => ({ ...f, party_b: e.target.value }))} placeholder="Counterparty" data-testid="form-party-b" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Contract Type</Label>
                <Select value={form.contract_type} onValueChange={(v) => setForm((f) => ({ ...f, contract_type: v }))}>
                  <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONTRACT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} data-testid="form-start-date" />
              </div>
              <div className="space-y-2">
                <Label>Expiration Date *</Label>
                <Input type="date" value={form.expiration_date} onChange={(e) => setForm((f) => ({ ...f, expiration_date: e.target.value }))} data-testid="form-expiration-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Attach Document</Label>
                <Input type="file" onChange={(e) => setFileToUpload(e.target.files?.[0] || null)} className="cursor-pointer" data-testid="form-file" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={3} placeholder="Additional notes..." data-testid="form-notes" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-contract-btn">{saving ? 'Saving...' : editingId ? 'Update Contract' : 'Create Contract'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-lg">
          {selectedContract && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSignature className="h-5 w-5 text-indigo-600" />
                  {selectedContract.title}
                </DialogTitle>
                <DialogDescription>Contract #{selectedContract.document_number}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Party A</p>
                    <p className="text-sm font-medium text-slate-900">{selectedContract.party_a}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Party B</p>
                    <p className="text-sm font-medium text-slate-900">{selectedContract.party_b}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Type</p>
                    <p className="text-sm font-medium text-slate-900 capitalize">{selectedContract.contract_type?.replace(/_/g, ' ')}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Status</p>
                    <Badge className={`${STATUS_BADGE[selectedContract.status] || ''} border-0 capitalize`}>{selectedContract.status}</Badge>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Start Date</p>
                    <p className="text-sm font-medium text-slate-900">{selectedContract.start_date && format(parseISO(selectedContract.start_date), 'MMM d, yyyy')}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Expiration Date</p>
                    <p className="text-sm font-medium text-slate-900">{selectedContract.expiration_date && format(parseISO(selectedContract.expiration_date), 'MMM d, yyyy')}</p>
                    <SeverityBadge severity={selectedContract.expiration_severity} daysRemaining={selectedContract.days_remaining} />
                  </div>
                </div>
                {selectedContract.notes && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Notes</p>
                    <p className="text-sm text-slate-700 whitespace-pre-line">{selectedContract.notes}</p>
                  </div>
                )}
                {selectedContract.file_path && (
                  <Button variant="outline" className="w-full" onClick={() => handleDownloadFile(selectedContract.id, selectedContract.original_filename)}>
                    <Download className="mr-2 h-4 w-4" /> Download Attached File
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractsPage;
